/**
 * item_change control for one editable General-Process column — mirrors the
 * transaction designer's Property-Panel item_change orchestration: pick a mode
 * (Rule | Business Logic | Call API) and edit it in the SAME reused editors
 * (`ItemChangeRuleEditor` / `ItemChangeCodeEditor`). The chosen rule is stored
 * on `process.item_changes[col]` and fires when that column is edited on the
 * result grid, setting/locking other columns in the row.
 */

import { useState } from 'react'
import { Code2, Pencil, Trash2 } from 'lucide-react'
import type { ItemChange } from '@/pages/designers/transaction/types'
import { normalizeItemChange, itemChangeSummary } from '@/pages/designers/transaction/types'
import ItemChangeRuleEditor from '@/pages/designers/transaction/ItemChangeRuleEditor'
import ItemChangeCodeEditor from '@/pages/designers/transaction/ItemChangeCodeEditor'
import { hintStyle } from './ui'

const MODE_LABEL: Record<'rule' | 'business_logic' | 'rest_api', string> = {
  rule: 'Rule', business_logic: 'Business Logic', rest_api: 'Call API',
}

interface Props {
  triggerColumn: string
  value: ItemChange | string | null | undefined
  /** Result columns offered as fill targets / {form.X} tokens. */
  formColumns: string[]
  /** Criteria names, offered as {header.X} tokens. */
  headerColumns?: string[]
  project: string
  onChange: (ic: ItemChange) => void
  onClear: () => void
  /** Deep-link a business_logic function name to the source code editor. */
  onOpenCode?: (method: string) => void
}

export default function ItemChangeField({
  triggerColumn, value, formColumns, headerColumns = [], project,
  onChange, onClear, onOpenCode,
}: Props) {
  const [ruleOpen, setRuleOpen] = useState(false)
  const [codeMode, setCodeMode] = useState<null | 'business_logic' | 'rest_api'>(null)

  const ic = normalizeItemChange(value)
  const mode: 'rule' | 'business_logic' | 'rest_api' | null =
    ic?.mode === 'rule' ? 'rule'
      : ic?.mode === 'rest_api' ? 'rest_api'
        : ic?.mode === 'business_logic' ? 'business_logic' : null
  const summary = itemChangeSummary(value)
  const blMethod = ic?.mode === 'business_logic' ? (ic.business_logic?.method || '') : ''

  const openExisting = () => {
    if (mode === 'rule') setRuleOpen(true)
    else if (mode) setCodeMode(mode)
  }

  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 6,
      padding: 10, marginTop: 8, background: 'var(--color-bg-secondary)',
    }}>
      <div style={{ fontSize: 13.5, marginBottom: 6 }}>
        When <code>{triggerColumn}</code> changes:
      </div>

      {mode ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            fontSize: 13, fontWeight: 600, padding: '1px 8px', borderRadius: 10,
            background: 'var(--color-accent)', color: '#fff',
          }}>{MODE_LABEL[mode]}</span>
          <span style={{ ...hintStyle, marginTop: 0, flex: 1, minWidth: 120 }}>{summary}</span>
          <button className="btn btn-secondary btn-sm" onClick={openExisting}>
            <Pencil size={13} /> Edit
          </button>
          {mode === 'business_logic' && onOpenCode && (
            <button className="btn btn-ghost btn-sm" onClick={() => onOpenCode(blMethod)}>
              <Code2 size={13} /> Code
            </button>
          )}
          <button className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={onClear}>
            <Trash2 size={13} /> Remove
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...hintStyle, marginTop: 0 }}>Add a rule:</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setRuleOpen(true)}>Rule</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCodeMode('business_logic')}>Business Logic</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setCodeMode('rest_api')}>Call API</button>
        </div>
      )}

      <ItemChangeRuleEditor
        open={ruleOpen}
        onClose={() => setRuleOpen(false)}
        value={ic?.mode === 'rule' ? ic : null}
        triggerColumnName={triggerColumn}
        formLevel={false}
        formColumnNames={formColumns}
        headerColumnNames={headerColumns}
        project={project}
        onSave={(r) => { onChange(r); setRuleOpen(false) }}
      />
      <ItemChangeCodeEditor
        open={codeMode !== null}
        onClose={() => setCodeMode(null)}
        value={ic && (ic.mode === 'business_logic' || ic.mode === 'rest_api') ? ic : null}
        initialMode={codeMode || 'business_logic'}
        formColumnNames={formColumns}
        headerColumnNames={headerColumns}
        project={project}
        onOpenCode={onOpenCode}
        onSave={(v) => { onChange(v); setCodeMode(null) }}
      />
    </div>
  )
}
